import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { LocaleContext } from '@/i18n/context';
import {
  detectLocale,
  setActiveLocale,
  storeLocale,
  storedLocale,
  translate,
  type Locale,
} from '@/i18n/locale';
import type { MessageKey, Params } from '@/i18n/types';
import { useSession } from '@/hooks/useSession';
import { writeStoredLocale } from '@/lib/localeStore';

/**
 * Decides which language the interface is in.
 *
 * The order is deliberate. A signed-in account's own setting wins outright, so
 * someone who chose Russian sees Russian on a borrowed English laptop — the
 * same reason reminders are addressed by account rather than by device. Only
 * when there is no account does a guest's remembered choice apply, and only
 * then the browser's own preference.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { data: user } = useSession();
  const [guestLocale, setGuestLocale] = useState<Locale>(
    () => storedLocale() ?? detectLocale(),
  );

  const locale = user?.language ?? guestLocale;

  useEffect(() => {
    // `apiFetch` reads this for `Accept-Language`, which is what lets the API
    // answer with validation errors in the language already on screen.
    setActiveLocale(locale);
    document.documentElement.lang = locale;

    // Remembered so the sign-in screen stays in the language of the account
    // that last used this browser, and mirrored where the worker can read it.
    storeLocale(locale);
    void writeStoredLocale(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setGuestLocale(next);
  }, []);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t: (key: MessageKey, params?: Params) => translate(locale, key, params),
    }),
    [locale, setLocale],
  );

  return <LocaleContext value={value}>{children}</LocaleContext>;
}
